import { TextClassContext } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import { resolveTextClassColor, type ColorScheme } from '@/lib/theme';
import {
  FontAwesome6Pro,
  type FontAwesome6ProRegularIconName,
  type FontAwesome6ProSolidIconName,
} from '@react-native-vector-icons/fontawesome6-pro';
import { cssInterop, useColorScheme } from 'nativewind';
import * as React from 'react';
import type { ComponentProps } from 'react';
import type { StyleProp, TextStyle } from 'react-native';

type IconBaseProps = {
  className?: string;
  size?: number;
  color?: string;
  style?: StyleProp<TextStyle>;
};

type IconProps =
  | (IconBaseProps & {
      name: FontAwesome6ProRegularIconName;
      iconStyle?: 'regular';
    })
  | (IconBaseProps & {
      name: FontAwesome6ProSolidIconName;
      iconStyle: 'solid';
    });

type FaProps = ComponentProps<typeof FontAwesome6Pro>;

function IconImplBase(props: FaProps & { className?: string }) {
  return <FontAwesome6Pro {...props} />;
}

const IconImpl = cssInterop(IconImplBase, {
  className: {
    target: 'style',
    nativeStyleToProp: {
      height: 'size',
      width: 'size',
    },
  },
});

/**
 * FontAwesome 6 Pro icon wrapper.
 *
 * FA icons render as RN `Text` with a custom font. The vector-icons library
 * defaults `color` to `"black"`, which breaks Tailwind `text-*` on web.
 * Semantic `text-*` classes are resolved via THEME (text-primary → primary).
 *
 * @example
 * ```tsx
 * <Icon name="house" iconStyle="solid" className="text-primary" size={16} />
 * ```
 */
function Icon({ name, className, size = 14, iconStyle = 'regular', color, style }: IconProps) {
  const { colorScheme } = useColorScheme();
  const scheme = (colorScheme ?? 'light') as ColorScheme;
  const textClass = React.useContext(TextClassContext);
  const mergedClassName = cn('text-foreground', textClass, className);
  const resolvedColor = color ?? resolveTextClassColor(mergedClassName, scheme);

  const shared = {
    size,
    color: resolvedColor,
    style,
  };

  if (iconStyle === 'solid') {
    return <IconImpl name={name} iconStyle="solid" {...shared} />;
  }

  return <IconImpl name={name} {...shared} />;
}

export { Icon };
export type { IconProps };
